import React, { useState } from 'react';
import { useAuth, history } from 'ice';
import { message, Alert } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { ProFormCheckbox, ProFormText, LoginForm } from '@ant-design/pro-form';
import type { LoginParams, LoginResult } from '@/interfaces/user';
import { login, fetchUserInfo } from '@/services/user';
import store from '@/store';
import logo from '@/assets/logo.png';
import $i18n from '@/i18n';
import { handleRedirect } from '@/utils';
import styles from './index.module.less';

const LoginMessage: React.FC<{
  content: string;
}> = ({ content }) => {
  return (
    <Alert
      style={{
        marginBottom: 24,
      }}
      message={content}
      type="error"
      showIcon
    />
  );
};

const Login: React.FC = () => {
  const [loginResult, setLoginResult] = useState<LoginResult>({});
  const [, userDispatcher] = store.useModel('user');
  const [, spaceRoleDispatcher] = store.useModel('spaceRole');
  const [, setAuth] = useAuth();

  async function updateUserInfo() {
    const userInfo = await fetchUserInfo();
    if (userInfo?.status === 4) {
      history?.push('/login?safetyModalVisible=true');
      return;
    }
    userDispatcher.updateCurrentUser(userInfo);
    return {
      _spaceId: userInfo?.defaultSpace?.spaceId,
      isAliyunPaymentUser: userInfo?.isAliyunPaymentUser,
      isDuibaUser: userInfo?.isDuibaUser,
      accountSrc: userInfo?.accountSrc,
    };
  }

  async function handleSubmit(values: LoginParams) {
    try {
      const result = await login(values);
      if (result.success) {
        message.success($i18n.get({ id: 'aopf.pages.login.LoginSucceeded', dm: '登录成功！' }));
        setAuth({
          admin: result.userType === 'admin',
          user: result.userType === 'user',
        });
        const { _spaceId, isDuibaUser } = await updateUserInfo() || {};
        handleRedirect(_spaceId, isDuibaUser, spaceRoleDispatcher);
      }
      // 如果失败去设置用户错误信息，显示提示信息
      setLoginResult(result);
    } catch (error) {
      message.error(
        $i18n.get({ id: 'aopf.pages.login.LoginFailedPleaseTryAgain', dm: '登录失败，请重试！' }),
      );
    }
  }
  return (
    <div className={styles.container}>
      <LoginForm
        title="ICE Pro"
        logo={<img alt="logo" src={logo} />}
        subTitle={$i18n.get({
          id: 'aopf.pages.login.OutOfTheBoxMid',
          dm: '基于 ice.js 3 开箱即用的中后台模板',
        })}
        onFinish={async (values) => {
          await handleSubmit(values as LoginParams);
        }}
      >
        {loginResult.success === false && (
          <LoginMessage
            content={$i18n.get({
              id: 'aopf.pages.login.AccountOrPasswordErrorAdmin',
              dm: '账户或密码错误(admin/ice)',
            })}
          />
        )}

        <ProFormText
          name="username"
          fieldProps={{
            size: 'large',
            prefix: <UserOutlined className={'prefixIcon'} />,
          }}
          placeholder={$i18n.get({
            id: 'aopf.pages.login.UsernameAdminOrUser',
            dm: '用户名: admin or user',
          })}
          rules={[
            {
              required: true,
              message: $i18n.get({
                id: 'aopf.pages.login.PleaseEnterAUsername',
                dm: '请输入用户名!',
              }),
            },
          ]}
        />

        <ProFormText.Password
          name="password"
          fieldProps={{
            size: 'large',
            prefix: <LockOutlined className={'prefixIcon'} />,
          }}
          placeholder={$i18n.get({ id: 'aopf.pages.login.PasswordIce', dm: '密码: ice' })}
          rules={[
            {
              required: true,
              message: $i18n.get({
                id: 'aopf.pages.login.PleaseEnterThePassword',
                dm: '请输入您的密码',
              }),
            },
          ]}
        />

        <div
          style={{
            marginBottom: 24,
          }}
        >
          <ProFormCheckbox noStyle name="autoLogin">
            {$i18n.get({ id: 'aopf.pages.login.AutomaticLogon', dm: '自动登录' })}
          </ProFormCheckbox>
          <a
            style={{
              float: 'right',
            }}
          >
            {$i18n.get({ id: 'aopf.pages.login.ForgotPassword', dm: '忘记密码' })}
          </a>
        </div>
      </LoginForm>
    </div>
  );
};

export const getConfig = () => {
  return {
    title: $i18n.get({ id: 'aopf.pages.login.Login', dm: '登录' }),
  };
};

export default Login;
